import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth, isAdmin } from "../context/AuthContext";
import { t } from "../lib/i18n";

const LINKS = [
  { to: "/", label: t("nav.home"), id: "home" },
  { to: "/decouvrir", label: t("nav.discover"), id: "discover" },
  { to: "/evenements", label: t("nav.events"), id: "events" },
  { to: "/partenaires", label: t("nav.partners"), id: "partners" },
  { to: "/challenges", label: t("nav.challenges"), id: "challenges" },
  { to: "/createurs", label: t("nav.creators"), id: "creators" },
];

export const PublicLayout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <div className="vb-grain min-h-screen bg-[#030303] text-white">
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 h-16 lg:h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2">
            <span className="vb-display text-xl lg:text-2xl font-black tracking-tight">VYTALI</span>
            <span className="vb-display text-xl lg:text-2xl font-black text-[#FF2E63]">BOOTS</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${isActive ? "text-white" : "text-neutral-400 hover:text-white"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <>
                {isAdmin(user) && (
                  <Link to="/admin" data-testid="nav-link-admin" className="text-sm text-neutral-300 hover:text-white transition-colors">
                    {t("nav.admin")}
                  </Link>
                )}
                <Link to="/compte" data-testid="nav-link-account" className="text-sm text-neutral-300 hover:text-white transition-colors">
                  {t("nav.account")}
                </Link>
                <button onClick={onLogout} data-testid="nav-logout-btn" className="text-sm text-neutral-500 hover:text-white transition-colors">
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <Link to="/connexion" data-testid="nav-link-login" className="text-sm text-neutral-300 hover:text-white transition-colors">
                {t("nav.login")}
              </Link>
            )}
            <Link to="/rejoindre" data-testid="nav-join-btn" className="vb-btn vb-btn-primary !py-2.5 !px-6">
              {t("nav.join")}
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-white"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/10 bg-[#050505] px-6 py-6 space-y-4" data-testid="nav-mobile-panel">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.id}`}
                className="block text-base text-neutral-300 hover:text-white transition-colors"
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-4 border-t border-white/10 space-y-4">
              {user ? (
                <>
                  {isAdmin(user) && (
                    <Link to="/admin" onClick={() => setOpen(false)} data-testid="nav-mobile-link-admin" className="block text-base text-neutral-300">
                      {t("nav.admin")}
                    </Link>
                  )}
                  <Link to="/compte" onClick={() => setOpen(false)} data-testid="nav-mobile-link-account" className="block text-base text-neutral-300">
                    {t("nav.account")}
                  </Link>
                  <button onClick={onLogout} data-testid="nav-mobile-logout" className="block text-base text-neutral-500">
                    {t("nav.logout")}
                  </button>
                </>
              ) : (
                <Link to="/connexion" onClick={() => setOpen(false)} data-testid="nav-mobile-link-login" className="block text-base text-neutral-300">
                  {t("nav.login")}
                </Link>
              )}
              <Link to="/rejoindre" onClick={() => setOpen(false)} data-testid="nav-mobile-join" className="vb-btn vb-btn-primary w-full">
                {t("nav.join")}
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="pt-16 lg:pt-20">{children}</main>

      <footer className="border-t border-white/10 mt-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16 grid gap-10 md:grid-cols-3">
          <div>
            <div className="vb-display text-2xl font-black">
              VYTALI<span className="text-[#FF2E63]">BOOTS</span>
            </div>
            <p className="mt-4 text-sm text-neutral-500 max-w-sm leading-relaxed">
              VytaliBoots capte, amplifie et préserve les moments réels de Vytali : les gens, les
              partenaires, les événements et les expériences.
            </p>
          </div>
          <div className="space-y-3">
            <div className="vb-label">Explorer</div>
            {LINKS.slice(1).map((l) => (
              <Link key={l.to} to={l.to} data-testid={`footer-link-${l.id}`} className="block text-sm text-neutral-400 hover:text-white transition-colors">
                {l.label}
              </Link>
            ))}
          </div>
          <div className="space-y-3">
            <div className="vb-label">Participer</div>
            <Link to="/rejoindre" data-testid="footer-link-join" className="block text-sm text-neutral-400 hover:text-white transition-colors">
              Rejoindre VytaliBoots
            </Link>
            <Link to="/connexion" data-testid="footer-link-login" className="block text-sm text-neutral-400 hover:text-white transition-colors">
              Connexion
            </Link>
          </div>
        </div>
        <div className="border-t border-white/5 py-6 text-center text-xs text-neutral-600">
          © {new Date().getFullYear()} VytaliBoots — Écosystème Vytali · fr-CA
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
